// app/javascript/controllers/guest_list_controller.js
import { Controller } from "@hotwired/stimulus"
import * as bootstrap from "bootstrap"

export default class extends Controller {
    static targets = [
        "form", "firstName", "lastName", "email",
        "tableBody", "emptyBox",
        "continueBtn", "skipBtn",
        "editModal", "editFirst", "editLast", "editEmail", "editSaveBtn", "tableSection"
    ]
    static values = { draftId: Number, draftSgid: String, invitationSlug: String, fields: Object }

    connect() {
        // cache fields (never null)
        this._cached = this.hasFieldsValue && this.fieldsValue ? structuredClone(this.fieldsValue) : {}
        if (!this._cached.guests) this._cached.guests = []

        // hydrate UI
        this.renderTable(this._cached.guests)
        this._toggleSections()               // <-- decide which view to show

        // wire add form
        this.formTarget?.addEventListener("submit", (e) => {
            e.preventDefault()
            this.addGuest()
        })
    }

    // ---------- UI helpers ----------
    renderTable(guests) {
        const has = !!guests && guests.length > 0

        // empty vs table
        this.emptyBoxTarget?.classList.toggle("d-none", has)
        this.tableSectionTarget?.classList.toggle("d-none", !has)

        // count badge (if present)
        const countEl = document.getElementById("guest-count")
        if (countEl) countEl.textContent = guests.length

        if (!this.hasTableBodyTarget) return
        this.tableBodyTarget.innerHTML = ""

        guests.forEach((g, idx) => {
            const rowDesktop = document.createElement("div")
            rowDesktop.className = "gl-row d-none d-md-grid"
            rowDesktop.innerHTML = `
  <div class="gl-td">${this._esc(g.first_name)}</div>
  <div class="gl-td">${this._esc(g.last_name)}</div>
  <div class="gl-td">${this._esc(g.email)}</div>
  <div class="gl-td gl-actions">
    <button ... data-action="guest-list#remove" data-index="${idx}" class="btn icon-btn p-0 bg-transparent">
      <i class="bi bi-trash3"></i>
    </button>
    <button ... data-action="guest-list#openEdit" data-index="${idx}" class="btn icon-btn p-0 bg-transparent">
      <i class="bi bi-pencil-square"></i>
    </button>
  </div>
`
            rowDesktop.className = "cell d-none d-md-flex flex-column flex-md-row align-items-start align-items-md-center"
            rowDesktop.dataset.index = idx
            rowDesktop.innerHTML = `
        <div class="col-md-4 mt-4 mb-2 mb-md-0"><p class="p4">${this._esc(g.first_name)}</p></div>
        <div class="col-md-2 mt-4 mb-2 mb-md-0"><p class="p4">${this._esc(g.last_name)}</p></div>
        <div class="col-md-3 mt-4 mb-2 mb-md-0"><p class="p5">${this._esc(g.email)}</p></div>
        <div class="col-md-1 text-end d-flex d-md-block gap-5">
          <button type="button" class="btn icon-btn btn-sm p-0 border-0 bg-transparent" data-action="guest-list#remove" data-index="${idx}">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <path d="M14.5984 7.87891V15.4689C14.5984 16.6389 13.6484 17.5789 12.4884 17.5789H7.50844C6.33844 17.5789 5.39844 16.6289 5.39844 15.4689V7.87891" stroke="#08466D" style="stroke:#08466D;stroke:color(display-p3 0.0314 0.2745 0.4275);stroke-opacity:1;" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M8.5 9.60938V14.0594" stroke="#08466D" style="stroke:#08466D;stroke:color(display-p3 0.0314 0.2745 0.4275);stroke-opacity:1;" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M11.5 9.60938V14.0594" stroke="#08466D" style="stroke:#08466D;stroke:color(display-p3 0.0314 0.2745 0.4275);stroke-opacity:1;" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M4 5.30078H16" stroke="#08466D" style="stroke:#08466D;stroke:color(display-p3 0.0314 0.2745 0.4275);stroke-opacity:1;" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M7.12109 5.30188C7.12109 3.71188 8.41109 2.42188 10.0011 2.42188C11.5911 2.42188 12.8811 3.71188 12.8811 5.30188" stroke="#08466D" style="stroke:#08466D;stroke:color(display-p3 0.0314 0.2745 0.4275);stroke-opacity:1;" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
          </button>
          <button type="button" class="btn icon-btn btn-sm p-0 border-0 bg-transparent" data-action="guest-list#openEdit" data-index="${idx}">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <path d="M16.8497 11.6509V16.5609C16.8497 17.1309 16.3897 17.5909 15.8197 17.5909H3.20969C2.63969 17.5909 2.17969 17.1309 2.17969 16.5609V5.24094C2.17969 4.67094 2.63969 4.21094 3.20969 4.21094H8.90969" stroke="#08466D" style="stroke:#08466D;stroke:color(display-p3 0.0314 0.2745 0.4275);stroke-opacity:1;" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M12.0813 13.2781L8.80125 14.5181C8.43125 14.6581 8.03125 14.3781 8.03125 13.9881V10.3481C8.03125 10.2281 8.08125 10.1081 8.15125 10.0181L13.5213 2.64812C13.7013 2.39812 14.0613 2.33812 14.3113 2.51812L17.5913 4.90812C17.8413 5.08812 17.9012 5.44812 17.7212 5.69812L12.3513 13.0781C12.2813 13.1681 12.1913 13.2381 12.0913 13.2781H12.0813Z" stroke="#08466D" style="stroke:#08466D;stroke:color(display-p3 0.0314 0.2745 0.4275);stroke-opacity:1;" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M11.8516 4.91797L16.1016 7.91797" stroke="#08466D" style="stroke:#08466D;stroke:color(display-p3 0.0314 0.2745 0.4275);stroke-opacity:1;" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
          </button>
        </div>
      `
            const rowMobile = document.createElement("div")
            rowMobile.className = "cell d-flex d-md-none flex-column flex-md-row align-items-start align-items-md-center"
            rowMobile.dataset.index = idx
            rowMobile.innerHTML = `
        <div class="p-3 border-bottom w-100 d-flex flex-row align-items-center justify-content-between">
          <div class="d-flex flex-column align-items-center justify-content-center">
            <p class="p4 m-0">${this._esc(g.first_name)} ${this._esc(g.last_name)}</p>
            <p class="p5 m-0 text-muted">${this._esc(g.email)}</p>
          </div>
          <div class="d-flex justify-content-end gap-2">
            <button type="button" class="btn icon-btn btn-sm p-0 border-0 bg-transparent text-primary" data-action="guest-list#remove" data-index="${idx}">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <path d="M14.5984 7.87891V15.4689C14.5984 16.6389 13.6484 17.5789 12.4884 17.5789H7.50844C6.33844 17.5789 5.39844 16.6289 5.39844 15.4689V7.87891" stroke="#08466D" style="stroke:#08466D;stroke:color(display-p3 0.0314 0.2745 0.4275);stroke-opacity:1;" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M8.5 9.60938V14.0594" stroke="#08466D" style="stroke:#08466D;stroke:color(display-p3 0.0314 0.2745 0.4275);stroke-opacity:1;" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M11.5 9.60938V14.0594" stroke="#08466D" style="stroke:#08466D;stroke:color(display-p3 0.0314 0.2745 0.4275);stroke-opacity:1;" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M4 5.30078H16" stroke="#08466D" style="stroke:#08466D;stroke:color(display-p3 0.0314 0.2745 0.4275);stroke-opacity:1;" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M7.12109 5.30188C7.12109 3.71188 8.41109 2.42188 10.0011 2.42188C11.5911 2.42188 12.8811 3.71188 12.8811 5.30188" stroke="#08466D" style="stroke:#08466D;stroke:color(display-p3 0.0314 0.2745 0.4275);stroke-opacity:1;" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
            </button>
            <button type="button" class="btn icon-btn btn-sm p-0 border-0 bg-transparent text-primary" data-action="guest-list#openEdit" data-index="${idx}">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <path d="M16.8497 11.6509V16.5609C16.8497 17.1309 16.3897 17.5909 15.8197 17.5909H3.20969C2.63969 17.5909 2.17969 17.1309 2.17969 16.5609V5.24094C2.17969 4.67094 2.63969 4.21094 3.20969 4.21094H8.90969" stroke="#08466D" style="stroke:#08466D;stroke:color(display-p3 0.0314 0.2745 0.4275);stroke-opacity:1;" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M12.0813 13.2781L8.80125 14.5181C8.43125 14.6581 8.03125 14.3781 8.03125 13.9881V10.3481C8.03125 10.2281 8.08125 10.1081 8.15125 10.0181L13.5213 2.64812C13.7013 2.39812 14.0613 2.33812 14.3113 2.51812L17.5913 4.90812C17.8413 5.08812 17.9012 5.44812 17.7212 5.69812L12.3513 13.0781C12.2813 13.1681 12.1913 13.2381 12.0913 13.2781H12.0813Z" stroke="#08466D" style="stroke:#08466D;stroke:color(display-p3 0.0314 0.2745 0.4275);stroke-opacity:1;" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M11.8516 4.91797L16.1016 7.91797" stroke="#08466D" style="stroke:#08466D;stroke:color(display-p3 0.0314 0.2745 0.4275);stroke-opacity:1;" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
            </button>
          </div>
        </div>
      `
            this.tableBodyTarget.appendChild(rowMobile)
            this.tableBodyTarget.appendChild(rowDesktop)
        })

        // enable Continue (optional page – always enabled)
        this._enableContinue(true)
    }

    _enableContinue(on) {
        this.continueBtnTargets.forEach(b => { b.disabled = !on; b.classList.toggle("grey-button", !on); b.classList.toggle("blue-button", on) })
    }

    _esc(s) { return (s ?? "").toString().replace(/[&<>"']/g, m => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[m])) }

    // ---------- CRUD ----------
    addGuest() {
        const first = this.firstNameTarget.value.trim()
        const last  = this.lastNameTarget.value.trim()
        const email = this.emailTarget.value.trim()

        if (!first || !last || !email) return

        const newGuest = { first_name: first, last_name: last, email }
        const merged = [...this._cached.guests, newGuest]
        this._persistGuests(merged, () => {
            this.formTarget.reset()
            this.renderTable(merged)
        })
    }

    remove(e) {
        const idx = parseInt(e.currentTarget.dataset.index, 10)
        if (Number.isNaN(idx)) return
        const merged = this._cached.guests.filter((_, i) => i !== idx)
        this._persistGuests(merged, () => {
            this.formTarget.reset()
            this.renderTable(merged)   // will also _toggle
        })
    }

    // place with other helpers
    _toggleSections() {
        const hasGuests = (this._cached?.guests?.length || 0) > 0
        if (hasGuests) this._showTable()
        else this._showEmpty()
    }

    removeAll = () => this._persistGuests([], () => this.renderTable([]))

    // ----- Edit modal -----
    openEdit(e) {
        const idx = parseInt(e.currentTarget.dataset.index, 10)
        if (Number.isNaN(idx)) return
        this._editIndex = idx
        const g = this._cached.guests[idx]
        if (!g) return

        this.editFirstTarget.value = g.first_name || ""
        this.editLastTarget.value  = g.last_name || ""
        this.editEmailTarget.value = g.email || ""

        // bootstrap modal
        const m = bootstrap.Modal.getOrCreateInstance(this.editModalTarget)
        m.show()
    }

    saveEdit() {
        const idx = this._editIndex
        if (idx == null) return
        const guests = [...this._cached.guests]
        const g = guests[idx]
        if (!g) return

        g.first_name = this.editFirstTarget.value.trim()
        g.last_name  = this.editLastTarget.value.trim()
        g.email      = this.editEmailTarget.value.trim()

        this._persistGuests(guests, () => {
            this.renderTable(guests)
            const m = bootstrap.Modal.getOrCreateInstance(this.editModalTarget)
            m.hide()
        })
    }

    // ---------- Persistence ----------
    async _persistGuests(guests, onOk) {
        // merge into cached fields safely
        const payload = structuredClone(this._cached)
        payload.guests = guests

        // PATCH merged to draft (server merges again to be safe)
        if (!this.hasDraftIdValue || !this.draftIdValue) { this._cached = payload; onOk?.(); return }

        const res = await fetch(`/party_drafts/${this.draftIdValue}`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
            },
            body: JSON.stringify({ fields_json: payload })
        })
        if (res.ok) {
            const json = await res.json().catch(() => ({}))
            this._cached = json?.fields_json || payload
            onOk?.()
        }
    }

    _showEmpty() {
        this.emptyBoxTarget.classList.remove('d-none')
        this.tableSectionTarget.classList.add('d-none')
    }
    _showTable() {
        this.emptyBoxTarget.classList.add('d-none')
        this.tableSectionTarget.classList.remove('d-none')
    }

    // ---------- Nav ----------
    back() {
        const sgidOrId = (this.hasDraftSgidValue && this.draftSgidValue)
            ? `draft_sgid=${encodeURIComponent(this.draftSgidValue)}`
            : `draft_id=${encodeURIComponent(this.draftIdValue)}`
        const slug = this.hasInvitationSlugValue ? `&invitation=${encodeURIComponent(this.invitationSlugValue)}` : ""
        window.location.assign(`/party/additional-info?${sgidOrId}${slug}`)
    }

    skip() { this.continue() }

    async continue() {
        // no hard validation; still persist current list just in case user typed something and didn’t press Add
        if (this.firstNameTarget?.value || this.lastNameTarget?.value || this.emailTarget?.value) {
            // do nothing (require explicit “Add”); we won’t auto-create partial guest
        }
        const sgidOrId = (this.hasDraftSgidValue && this.draftSgidValue)
            ? `draft_sgid=${encodeURIComponent(this.draftSgidValue)}`
            : `draft_id=${encodeURIComponent(this.draftIdValue)}`
        window.location.assign(`/party/review?${sgidOrId}`)
    }
}